import type { EtaAlert, ScenarioResult } from '@/types/domain';
// Общее ядро расчётов с edge-функциями (комиссии, маржа, капитал) — одна реализация на всех.
import {
  computeCapitalRequirement,
  leverageForBase,
  MIN_APR_DAYS,
  shortMarginPerUnitUsd,
  takerFee,
} from '../../supabase/functions/_shared/etaCore';

// Локальный пересчёт — только fallback для старых алертов без серверных arb_* расчётов.
const DEFAULT_QTY_BASE = 1;
const DEFAULT_LIQ_BUFFER_PCT = 0.15;

function finitePositive(value: number | null | undefined): number | null {
  return value != null && Number.isFinite(value) && value > 0 ? value : null;
}

function resolveSize(alert: EtaAlert, side: 'min_bid' | 'max_bid' | 'min_ask' | 'max_ask'): number | null {
  const baseSizeField = `${side}_size_base` as const;
  const sizeField = `${side}_size` as const;
  return finitePositive(alert[baseSizeField]) || finitePositive(alert[sizeField]);
}

function calcFeesUsd(buyAsk: number, sellBid: number, qtyBase: number, spot: number | null): number {
  return (takerFee(undefined, buyAsk, spot) + takerFee(undefined, sellBid, spot)) * qtyBase;
}

function chooseQty(askQty: number | null, bidQty: number | null): { qty: number; warning?: string } {
  if (askQty != null && bidQty != null) return { qty: Math.min(askQty, bidQty) };
  if (askQty != null) return { qty: askQty };
  if (bidQty != null) return { qty: bidQty };
  return { qty: DEFAULT_QTY_BASE, warning: `Размеры стакана недоступны: использован условный размер ${DEFAULT_QTY_BASE}.` };
}

function inferSpotFromRaw(alert: EtaAlert): number | null {
  if (!alert.raw || typeof alert.raw !== 'object') return null;
  const values = Object.values(alert.raw as Record<string, { spot?: unknown }>);
  const spots = values
    .map((v) => (typeof v?.spot === 'number' && Number.isFinite(v.spot) && v.spot > 0 ? v.spot : null))
    .filter((v): v is number => v != null);
  if (spots.length === 0) return null;
  return spots.reduce((sum, item) => sum + item, 0) / spots.length;
}

function createScenario(
  buyExch: string | null,
  sellExch: string | null,
  buyAsk: number,
  buyAskSizeBase: number,
  buyAskSizeEstimated: boolean,
  sellBid: number,
  sellBidSizeBase: number,
  sellBidSizeEstimated: boolean,
  qtyBase: number,
  warnings: string[],
  spot: number | null,
): ScenarioResult {
  const grossUsd = (sellBid - buyAsk) * qtyBase;
  const feesUsd = calcFeesUsd(buyAsk, sellBid, qtyBase, spot);
  const netUsd = grossUsd - feesUsd;
  const premium = buyAsk * qtyBase;
  return {
    buyExch: buyExch ?? 'n/a',
    sellExch: sellExch ?? 'n/a',
    buyAsk,
    buyAskSizeBase,
    buyAskSizeEstimated,
    sellBid,
    sellBidSizeBase,
    sellBidSizeEstimated,
    qtyBase,
    grossUsd,
    feesUsd,
    netUsd,
    netPctOfPremium: premium > 0 ? (netUsd / premium) * 100 : 0,
    warnings: warnings.length > 0 ? warnings : undefined,
  };
}

function applyNoShortCapital(scenario: ScenarioResult): ScenarioResult {
  const capital = scenario.buyAsk * scenario.qtyBase;
  return {
    ...scenario,
    capitalRequiredUsd: capital,
    capitalLegs: [
      {
        exchange: scenario.buyExch,
        role: 'buy',
        usd: capital,
        note: 'премия',
      },
    ],
  };
}

function computeNoShortFromAlert(alert: EtaAlert): ScenarioResult | null {
  const maxBid = finitePositive(alert.max_bid);
  const minAsk = finitePositive(alert.min_ask);
  const hasBid = maxBid != null;
  const hasAsk = minAsk != null;

  if (!hasBid && !hasAsk) return null;

  const bidQty = resolveSize(alert, 'max_bid');
  const askQty = resolveSize(alert, 'min_ask');
  const { qty, warning: qtyWarning } = chooseQty(askQty, bidQty);
  const warnings: string[] = [];
  if (qtyWarning) warnings.push(qtyWarning);
  if (!qtyWarning && askQty == null && bidQty != null) {
    warnings.push(`Размер на бирже ${(alert.min_ask_exchange ?? alert.max_bid_exchange ?? 'n/a').toUpperCase()} недоступен - взят размер противоположной ноги.`);
  } else if (!qtyWarning && bidQty == null && askQty != null) {
    warnings.push(`Размер на бирже ${(alert.max_bid_exchange ?? alert.min_ask_exchange ?? 'n/a').toUpperCase()} недоступен - взят размер противоположной ноги.`);
  }

  if (!hasAsk && hasBid) {
    warnings.push('Нет ask на других биржах — арбитраж без шорта невозможен. Показан контекст по bid.');
    return applyNoShortCapital(createScenario(
      alert.max_bid_exchange,
      alert.max_bid_exchange,
      maxBid,
      qty,
      true,
      maxBid,
      qty,
      bidQty == null,
      qty,
      warnings,
      inferSpotFromRaw(alert),
    ));
  }

  if (hasAsk && !hasBid) {
    warnings.push('Нет bid на других биржах — арбитраж без шорта невозможен. Показан контекст по ask.');
    return applyNoShortCapital(createScenario(
      alert.min_ask_exchange,
      alert.min_ask_exchange,
      minAsk,
      qty,
      askQty == null,
      minAsk,
      qty,
      true,
      qty,
      warnings,
      inferSpotFromRaw(alert),
    ));
  }

  if (alert.min_ask_exchange && alert.max_bid_exchange && alert.min_ask_exchange === alert.max_bid_exchange) {
    warnings.push('Лучшая ask и лучшая bid пришли с одной биржи: межбиржевой арбитраж может быть недоступен.');
  }

  return applyNoShortCapital(createScenario(
    alert.min_ask_exchange,
    alert.max_bid_exchange,
    minAsk!,
    askQty ?? qty,
    askQty == null,
    maxBid!,
    bidQty ?? qty,
    bidQty == null,
    qty,
    warnings,
    inferSpotFromRaw(alert),
  ));
}

function computeWithShortFromAlert(alert: EtaAlert, daysToExpiry: number): ScenarioResult | null {
  const maxBid = finitePositive(alert.max_bid);
  const minAsk = finitePositive(alert.min_ask);
  const hasBid = maxBid != null;
  const hasAsk = minAsk != null;

  if (!hasBid && !hasAsk) return null;

  const bidQty = resolveSize(alert, 'max_bid');
  const askQty = resolveSize(alert, 'min_ask');
  const { qty, warning: qtyWarning } = chooseQty(askQty, bidQty);

  const warnings: string[] = [];
  if (qtyWarning) warnings.push(qtyWarning);
  if (!qtyWarning && askQty == null && bidQty != null) {
    warnings.push(`Размер на бирже ${(alert.min_ask_exchange ?? alert.max_bid_exchange ?? 'n/a').toUpperCase()} недоступен - взят размер противоположной ноги.`);
  } else if (!qtyWarning && bidQty == null && askQty != null) {
    warnings.push(`Размер на бирже ${(alert.max_bid_exchange ?? alert.min_ask_exchange ?? 'n/a').toUpperCase()} недоступен - взят размер противоположной ноги.`);
  }

  const buyAsk = minAsk ?? maxBid!;
  const sellBid = maxBid ?? minAsk!;

  if (!hasAsk && hasBid) {
    warnings.push('Ask недоступен: для расчёта использован bid как proxy цены покупки опциона.');
  }
  if (!hasBid && hasAsk) {
    warnings.push('Bid недоступен: для расчёта использован ask как proxy цены продажи/шорта опциона.');
  }

  const baseScenario = createScenario(
    alert.min_ask_exchange ?? alert.max_bid_exchange,
    alert.max_bid_exchange ?? alert.min_ask_exchange,
    buyAsk,
    askQty ?? qty,
    askQty == null,
    sellBid,
    bidQty ?? qty,
    bidQty == null,
    qty,
    warnings,
    inferSpotFromRaw(alert),
  );

  const spot = inferSpotFromRaw(alert) ?? finitePositive(alert.strike) ?? 1;
  if (inferSpotFromRaw(alert) == null) {
    baseScenario.warnings = [...(baseScenario.warnings ?? []), 'Spot недоступен: использован strike для margin/APR.'];
  }

  const premiumLockUsd = buyAsk * qty;
  const marginLockUsd = shortMarginPerUnitUsd(alert.option_type, spot, finitePositive(alert.strike) ?? spot, sellBid) * qty;
  const totalCapitalUsd = premiumLockUsd + marginLockUsd;
  const roiOnCapitalPct = totalCapitalUsd > 0 ? (baseScenario.netUsd / totalCapitalUsd) * 100 : 0;
  // Минимум 1 день, как на сервере: иначе в день экспирации APR улетает в десятки тысяч %.
  const normalizedDays = Number.isFinite(daysToExpiry) ? Math.max(daysToExpiry, MIN_APR_DAYS) : MIN_APR_DAYS;
  const aprPct = roiOnCapitalPct * (365 / normalizedDays);
  const liquidationPriceUsd = alert.option_type === 'C'
    ? spot * (1 + DEFAULT_LIQ_BUFFER_PCT)
    : Math.max(0, spot * (1 - DEFAULT_LIQ_BUFFER_PCT));

  return {
    ...baseScenario,
    capitalRequiredUsd: totalCapitalUsd,
    capitalLegs: [
      {
        exchange: baseScenario.buyExch,
        role: 'buy',
        usd: premiumLockUsd,
        note: 'премия',
      },
      {
        exchange: baseScenario.sellExch,
        role: 'sell',
        usd: marginLockUsd,
        note: 'маржа',
      },
    ],
    premiumLockUsd,
    marginLockUsd,
    totalCapitalUsd,
    roiOnCapitalPct,
    aprPct,
    liquidationPriceUsd,
  };
}

export interface ResolvedEtaScenarios {
  noShort: ScenarioResult | null;
  withShort: ScenarioResult | null;
  bestNet: number | null;
  bestApr: number | null;
  bestScenario: 'Без шорта' | 'Со шортом' | '—';
}

export function isScenarioExecutable(scenario: ScenarioResult | null): boolean {
  if (!scenario) return false;
  return Number.isFinite(scenario.buyAsk)
    && scenario.buyAsk > 0
    && Number.isFinite(scenario.sellBid)
    && scenario.sellBid > 0
    && Number.isFinite(scenario.qtyBase)
    && scenario.qtyBase > 0
    && scenario.buyExch !== 'n/a'
    && scenario.sellExch !== 'n/a';
}

export function hasExecutableEtaScenario(alert: EtaAlert): boolean {
  const { noShort, withShort } = resolveEtaScenarios(alert);
  return isScenarioExecutable(noShort) || isScenarioExecutable(withShort);
}

export function scaleScenarioByBalance(
  scenario: ScenarioResult,
  balanceUsd: number,
): ScenarioResult {
  const baseCapital = scenario.minRequiredUsd ?? scenario.capitalRequiredUsd ?? (scenario.buyAsk * scenario.qtyBase);
  if (!Number.isFinite(balanceUsd) || balanceUsd <= 0 || !Number.isFinite(baseCapital) || baseCapital <= 0) {
    return scenario;
  }

  const factor = Math.min(1, balanceUsd / baseCapital);
  if (!Number.isFinite(factor) || factor <= 0 || factor >= 1) return scenario;

  const scale = (v: number | undefined): number | undefined => (v == null ? undefined : v * factor);
  const capitalLegs = scenario.capitalLegs?.map((leg) => ({ ...leg, usd: leg.usd * factor }));
  const perExchangeRequirement = scenario.perExchangeRequirement
    ? Object.fromEntries(Object.entries(scenario.perExchangeRequirement).map(([exchange, usd]) => [exchange, usd * factor]))
    : undefined;
  const hedgeRequirement = scenario.hedgeRequirement
    ? { ...scenario.hedgeRequirement, usd: scenario.hedgeRequirement.usd * factor }
    : scenario.hedgeRequirement;
  const minRequiredUsd = scenario.minRequiredUsd != null ? scenario.minRequiredUsd * factor : undefined;
  return {
    ...scenario,
    qtyBase: scenario.qtyBase * factor,
    grossUsd: scenario.grossUsd * factor,
    feesUsd: scenario.feesUsd * factor,
    netUsd: scenario.netUsd * factor,
    capitalRequiredUsd: scale(scenario.capitalRequiredUsd),
    capitalLegs,
    premiumLockUsd: scale(scenario.premiumLockUsd),
    marginLockUsd: scale(scenario.marginLockUsd),
    totalCapitalUsd: scale(scenario.totalCapitalUsd),
    perExchangeRequirement,
    minRequiredUsd,
    hedgeRequirement,
    profitPer100Usd: minRequiredUsd && minRequiredUsd > 0 ? ((scenario.netUsd * factor) / minRequiredUsd) * 100 : undefined,
    fitsBalance: scenario.fitsBalance,
  };
}

export interface HedgeLeverageConfig {
  btc: number;
  eth: number;
  default: number;
}

export interface MinRequiredCapitalResult {
  minRequiredUsd: number;
  perExchangeRequirement: Record<string, number>;
  hedgeRequirement: { exchange: string; usd: number; leverage: number } | null;
  profitPer100Usd: number;
  fitsBalance: boolean;
}

export function computeMinRequiredCapital(
  scenario: ScenarioResult,
  leverageConfig: HedgeLeverageConfig,
  freeBalances?: Record<string, number>,
  // Базовый актив (BTC/ETH/…) для выбора плеча хеджа.
  base = '',
  hedgeEnabled = false,
): MinRequiredCapitalResult {
  // Та же функция, что использует theta-executor: UI и бот считают капитал одинаково.
  const cap = computeCapitalRequirement(scenario, {
    hedgeEnabled,
    leverage: leverageForBase(base, leverageConfig),
  });
  const fitsBalance = freeBalances
    ? Object.entries(cap.perExchangeRequirement).every(([exchange, usd]) => (freeBalances[exchange] ?? 0) >= usd)
    : true;
  return { ...cap, fitsBalance };
}

export function resolveEtaScenarios(alert: EtaAlert, nowTs = Date.now()): ResolvedEtaScenarios {
  // Экспирация в 08:00 UTC (как на сервере).
  const daysToExpiry = (new Date(`${alert.expiry.slice(0, 10)}T08:00:00Z`).getTime() - nowTs) / 86_400_000;

  // Серверные сценарии (eta-spread-monitor) считаются по всем парам бирж и с комиссиями из
  // exchange_fees — они первичны. Локальный пересчёт по min_ask/max_bid — только fallback
  // для старых алертов, где arb_* не сохранены.
  const noShort = alert.arb_no_short ?? computeNoShortFromAlert(alert);
  const withShort = alert.arb_with_short ?? computeWithShortFromAlert(alert, daysToExpiry);

  const bestNetCandidates = [alert.arb_best_net_usd, noShort?.netUsd, withShort?.netUsd]
    .filter((v): v is number => v != null && Number.isFinite(v));
  const bestNet = bestNetCandidates.length > 0 ? Math.max(...bestNetCandidates) : null;

  const bestAprCandidates = [withShort?.aprPct, alert.arb_best_apr_pct]
    .filter((v): v is number => v != null && Number.isFinite(v));
  const bestApr = bestAprCandidates.length > 0 ? Math.max(...bestAprCandidates) : null;

  const noShortNet = noShort?.netUsd;
  const withShortNet = withShort?.netUsd;
  const bestScenario: 'Без шорта' | 'Со шортом' | '—' =
    noShortNet == null && withShortNet == null
      ? '—'
      : withShortNet == null
        ? 'Без шорта'
        : noShortNet == null
          ? 'Со шортом'
          : withShortNet > noShortNet
            ? 'Со шортом'
            : 'Без шорта';

  return {
    noShort,
    withShort,
    bestNet,
    bestApr,
    bestScenario,
  };
}
